<template>
  <div class="tiktok-analytics-public-summary">
    <el-row class="mb-x2" :gutter="16">
      <el-col :span="8">
        <AnalyticsTotal
          metric="応募数"
          :value="entryCount"
          tooltip="キャンペーンへの応募数です。この数値には、重複応募も含まれます。"
          :notationText="isCampaignCollectionCompleted || isCampaignFinalCollection ? '集計完了' : undefined"
        ></AnalyticsTotal>
      </el-col>
      <el-col :span="8">
        <AnalyticsTotal
          metric="応募者数"
          :value="applicantCount"
          :notationText="isCampaignCollectionCompleted || isCampaignFinalCollection ? '集計完了' : undefined"
          tooltip="キャンペーンへ応募したアカウントの数です。"
        ></AnalyticsTotal>
      </el-col>
      <el-col :span="8">
        <AnalyticsTotal
          metric="新規応募者数"
          :value="newApplicantCount"
          :notationText="isCampaignCollectionCompleted || isCampaignFinalCollection ? '集計完了' : undefined"
        >
          <template #tooltip-content>
            <el-tooltip class="ml-x1" placement="top">
              <div slot="content">
                同一開催アカウントで過去に実施したキャンペーン<br />
                (WEBインスタントウィン含む、インスタントウィン除く)<br />
                に応募した実績のない応募者の数です。
              </div>
              <i class="mr-x2 mdi mdi-help-circle"></i
            ></el-tooltip>
          </template>
        </AnalyticsTotal>
      </el-col>
    </el-row>
    <el-row class="mb-x2" :gutter="16">
      <el-col :span="8">
        <AnalyticsTotal
          metric="フォロワー増減数"
          :value="followerUpDown"
          tooltip="キャンペーン開催アカウントのフォロワー数の増減です。"
          :notationText="isCampaignCollectionCompleted || isCampaignFinalCollection ? '集計完了' : undefined"
          :isUpDown="true"
          :isTikTok="!(isCampaignCollectionCompleted || isCampaignFinalCollection)"
        ></AnalyticsTotal>
      </el-col>
      <el-col :span="8">
        <AnalyticsTotal
          metric="インプレッション"
          :value="impressionTotalCount"
          tooltip="動画が視聴された回数です。初回データは投稿から24~48時間経過後に取得されます。"
          :notationText="campaignStatusText"
        ></AnalyticsTotal>
      </el-col>
      <el-col :span="8">
        <AnalyticsTotal
          metric="リーチ"
          :value="reachCount"
          tooltip="動画を視聴した人の数です。初回データは投稿から24~48時間経過後に取得されます。"
          :notationText="campaignStatusText"
        ></AnalyticsTotal>
      </el-col>
    </el-row>
    <el-row class="mb-x2" :gutter="16">
      <el-col :span="8">
        <AnalyticsTotal
          metric="視聴完了数"
          :value="watchedFullVideoCount"
          tooltip="動画を最後まで視聴した人の数です。初回データは投稿から24~48時間経過後に取得されます。"
          :notationText="campaignStatusText"
        ></AnalyticsTotal>
      </el-col>
      <el-col :span="8">
        <AnalyticsTotal
          metric="当選数"
          :value="winnerCount"
          :notationText="isCampaignCollectionCompleted || isCampaignFinalCollection ? '集計完了' : undefined"
          :isTikTok="!(isCampaignCollectionCompleted || isCampaignFinalCollection)"
        ></AnalyticsTotal>
      </el-col>
    </el-row>
  </div>
</template>

<script lang="ts">
import { mapState } from 'pinia'
import { defineComponent } from 'vue'

import AnalyticsTotal from '@/client/components/_atoms/analytics_total'
import { CAMPAIGN_STATUS } from '@/client/constant'
import { useTikTokAnalyticsPublicStore } from '@/client/store/useTikTokAnalyticsPublicStore'
import { TTikTokCampaignAnalytic } from '@/typings/api/publics/tiktok_campaign_analytics.type'

export default defineComponent({
  components: { AnalyticsTotal },
  computed: {
    ...mapState(useTikTokAnalyticsPublicStore, {
      campaignAnalytics: state => state.campaignAnalytics,
      columnChartData: state => state.campaignAnalytics.slice(0, state.campaignAnalytics.length - 1),
      campaign: state => state.campaign
    }),

    isCampaignFinalCollection() {
      return this.campaign?.status === CAMPAIGN_STATUS.DURING_FINAL_COLLECTION
    },

    isCampaignCollectionCompleted() {
      return this.campaign?.status === CAMPAIGN_STATUS.COLLECTION_COMPLETED
    },

    campaignStatusText() {
      if (this.campaign?.status === CAMPAIGN_STATUS.DURING_FINAL_COLLECTION) {
        return '最終データ取得待ち'
      }

      if (this.campaign?.status === CAMPAIGN_STATUS.COLLECTION_COMPLETED) {
        return '集計完了'
      }

      return '開催中、24~48時間ごとに更新されます。'
    },

    entryCount() {
      return this.getTotal(this.columnChartData, 'entryCount')
    },

    applicantCount() {
      return this.getTotal(this.columnChartData, 'applicantCount')
    },

    newApplicantCount() {
      return this.getTotal(this.columnChartData, 'newApplicantCount')
    },

    followerUpDown() {
      const firstFollowerCount = this.getFirstValueExceptNull(this.campaignAnalytics, 'followerCount')

      return this.getLastValue(this.campaignAnalytics, 'followerCount') - firstFollowerCount
    },

    impressionTotalCount() {
      return this.getLastValue(this.campaignAnalytics, 'impressionTotalCount')
    },

    reachCount() {
      return this.getLastValue(this.campaignAnalytics, 'reachCount')
    },

    watchedFullVideoCount() {
      return this.getLastValue(this.campaignAnalytics, 'watchedFullVideoCount')
    },

    winnerCount() {
      return this.getTotal(this.columnChartData, 'winner_count')
    }
  },
  methods: {
    /**
     * 合計値を取得
     */
    getTotal(data: TTikTokCampaignAnalytic[], metric: string) {
      return this.addition(this.getMetricArray(data, metric))
    },

    /**
     * 足し算
     */
    addition(numbers: number[] = []): number | null {
      const values = numbers.filter(v => v !== null)

      if (!values.length) {
        return null
      }

      return numbers.map(v => this.toNumber(v)).reduce((a, c) => a + c, 0)
    },

    /**
     * 分析データから値の配列を取得
     */
    getMetricArray(insights: readonly any[], metric: string): any[] {
      return insights.map(insight => this.getMetricValue(insight, metric))
    },

    /**
     * 分析データから値を取得
     */
    getMetricValue(insight: any, metric: string): any | null {
      return isNaN(insight[metric]) ? null : insight[metric]
    },

    getFirstValueExceptNull(analytics: readonly any[], value: string) {
      const data = analytics.find(analytic => analytic[value] !== null)

      if (!data) {
        return 0
      }

      return data[value]
    },

    /**
     * 最後の値を取得
     */
    getLastValue(analytics: readonly any[], value: string) {
      const data = analytics.filter(analytic => analytic[value] !== null)

      if (data.length === 0) {
        return null
      }

      return data[data.length - 1][value]
    },

    /**
     * 数値変換
     */
    toNumber(value: any): number {
      return !isNaN(value) ? value : 0
    }
  }
})
</script>
